import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Typography,
} from "@mui/material";
import { Field } from "react-final-form";

import { Form, type FormProps, TextField } from "~ui/atoms/Form";

// Component strings
const tDistrict = "District";
const tSchoolName = "School Name";
const tState = "State";
const tStateCode = "State Code";
const tCity = "City";
const tZipCode = "ZIP Code";
const tZipPlus4 = "ZIP+4";
const tAddressLine1 = "Address Line 1";
const tAddressLine2 = "Address Line 2";
const tAddressLine3 = "Address Line 3";
const tPhoneNumber = "Phone Number";
const tWebsite = "Website";
const tSchoolLevel = "School Level";
const tRequired = "This field is required";
const tCreateSchool = "Create School";

// School level options
const tElementary = "Elementary School";
const tMiddle = "Middle School";
const tHigh = "High School";
const tK12 = "K-12";

export interface FormValues {
  districtId: string;
  schoolName: string;
  stateName: string;
  stateInitials: string;
  city: string;
  zipCode: string;
  zipCodePlus4: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  phoneNumber: string;
  website: string;
  level: string;
  lowestGrade: string;
  highestGrade: string;
  schoolType: string;
  isCharterSchool: boolean;
}

export interface District {
  id: string;
  name: string;
  bubbleId?: string;
}

export type CreateSchoolTemplateProps = Pick<FormProps, "debug"> & {
  initialValues?: FormValues;
  districts: District[];
  isLoadingDistricts: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<{ FORM_ERROR: string } | undefined>;
};

export const CreateSchoolTemplate = ({
  initialValues,
  districts,
  isLoadingDistricts,
  onClose: _onClose,
  onSubmit,
  ...props
}: CreateSchoolTemplateProps) => {
  const validateRequired = (value?: string) => {
    return value ? undefined : tRequired;
  };

  return (
    <Form<FormValues>
      {...props}
      initialValues={initialValues}
      onSubmit={(values, _form) => {
        return onSubmit(values);
      }}
    >
      {function WithForm({ submitting, submitError, handleSubmit }) {
        return (
          <>
            <Field name="districtId" validate={validateRequired}>
              {({ input, meta }) => (
                <FormControl error={meta.error && meta.touched} fullWidth margin="normal">
                  <InputLabel id="district-select-label">{tDistrict} *</InputLabel>
                  <MuiSelect
                    {...input}
                    disabled={submitting || isLoadingDistricts}
                    label={`${tDistrict} *`}
                    labelId="district-select-label"
                    required
                  >
                    {districts.map((district) => (
                      <MenuItem key={district.id} value={district.id}>
                        {district.name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                  {meta.error && meta.touched && (
                    <Typography color="error" variant="caption">
                      {meta.error}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Field>
            <TextField
              name="schoolName"
              required
              textFieldProps={{
                label: tSchoolName,
                margin: "normal",
              }}
              validate={validateRequired}
            />
            <Grid container spacing={2}>
              <Grid size={8}>
                <TextField
                  name="stateName"
                  required
                  textFieldProps={{
                    label: tState,
                    margin: "normal",
                  }}
                  validate={validateRequired}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  name="stateInitials"
                  required
                  textFieldProps={{
                    label: tStateCode,
                    margin: "normal",
                  }}
                  validate={validateRequired}
                />
              </Grid>
            </Grid>
            <TextField
              name="city"
              required
              textFieldProps={{
                label: tCity,
                margin: "normal",
              }}
              validate={validateRequired}
            />
            <Grid container spacing={2}>
              <Grid size={8}>
                <TextField
                  name="zipCode"
                  required
                  textFieldProps={{
                    label: tZipCode,
                    margin: "normal",
                  }}
                  validate={validateRequired}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  name="zipCodePlus4"
                  textFieldProps={{
                    label: tZipPlus4,
                    margin: "normal",
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              name="addressLine1"
              required
              textFieldProps={{
                label: tAddressLine1,
                margin: "normal",
              }}
              validate={validateRequired}
            />
            <TextField
              name="addressLine2"
              textFieldProps={{
                label: tAddressLine2,
                margin: "normal",
              }}
            />
            <TextField
              name="addressLine3"
              textFieldProps={{
                label: tAddressLine3,
                margin: "normal",
              }}
            />
            <TextField
              name="phoneNumber"
              textFieldProps={{
                label: tPhoneNumber,
                margin: "normal",
              }}
            />
            <TextField
              name="website"
              textFieldProps={{
                label: tWebsite,
                margin: "normal",
              }}
            />
            <Field name="level" validate={validateRequired}>
              {({ input, meta }) => (
                <FormControl error={meta.error && meta.touched} fullWidth margin="normal">
                  <InputLabel id="level-select-label">{tSchoolLevel} *</InputLabel>
                  <MuiSelect
                    {...input}
                    disabled={submitting}
                    label={`${tSchoolLevel} *`}
                    labelId="level-select-label"
                    required
                  >
                    <MenuItem value="elementary">{tElementary}</MenuItem>
                    <MenuItem value="middle">{tMiddle}</MenuItem>
                    <MenuItem value="high">{tHigh}</MenuItem>
                    <MenuItem value="k-12">{tK12}</MenuItem>
                  </MuiSelect>
                  {meta.error && meta.touched && (
                    <Typography color="error" variant="caption">
                      {meta.error}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Field>
            <Grid container sx={{ mt: 2 }}>
              <Grid size={12}>
                <Button
                  color="primary"
                  disabled={submitting}
                  fullWidth
                  onClick={(e) => handleSubmit(e)}
                  type="button"
                  variant="contained"
                >
                  {submitting ? <CircularProgress size={24} /> : tCreateSchool}
                </Button>
              </Grid>
            </Grid>
            {submitError && <Typography color="error">{submitError}</Typography>}
          </>
        );
      }}
    </Form>
  );
};
